import groupBy from 'lodash/groupBy';
import orderBy from 'lodash/orderBy';
import { useMemo } from 'react';
import { useQuery } from 'react-query';

import platformService from '../../core/services/platform.service';
import { useMe } from '../../profile/hooks';
import { GROUPS_COLLECTION, Group, GroupWithInvited, INVITED_COLLECTION, Invited } from '../types';
import { getGroupGuests, getMissingGroupFields, mergeGuests } from '../utils';

type UseGroupsResult = {
  groups: GroupWithInvited[];
  isLoading: boolean;
  refetch: () => void;
};

/** Groupe renvoyé par l'API avec ses invités imbriqués sous `children` */
type GroupWithChildren = Group & { children?: Invited[] };

/**
 * Récupère les groupes créés par l'utilisateur courant, enrichis de leurs invités.
 *
 * Un seul appel via `/me/children/{groups}?childrenCollection={invited}` :
 * - les groupes sont rattachés à l'utilisateur (`parentId === me._id`, filtré côté API) ;
 * - les invités arrivent imbriqués sous `children` (rattachés via `parentId === group._id`).
 *
 * Les invités saisis dans le formulaire du groupe mais dont la fiche n'existe pas
 * encore (formulaire non validé) sont ajoutés à la liste, marqués `isDraft`.
 */
export function useGroups(
  groupsCollection: string = GROUPS_COLLECTION,
  invitedCollection: string = INVITED_COLLECTION,
  guestsField?: string,
): UseGroupsResult {
  const me = useMe();
  const userId = me?._id;

  const { data: rawGroups = [], isLoading, refetch } = useQuery(
    ['group-visits', 'groups', groupsCollection, invitedCollection, userId],
    () =>
      platformService.fetchMyChildren(groupsCollection, invitedCollection) as Promise<
        GroupWithChildren[]
      >,
    { enabled: !!userId, refetchOnWindowFocus: true },
  );

  const groups = useMemo<GroupWithInvited[]>(() => {
    return (rawGroups || []).map((group) => {
      const invited = mergeGuests(group.children || [], getGroupGuests(group, guestsField));
      const registeredCount = invited.filter((i) => i.status === 'registered').length;
      const refusedCount = invited.filter((i) => i.status === 'refused').length;
      const pmrCount = invited.filter((i) => i.pmr === 'yes').length;
      const parkingCount = invited.filter((i) => i.parking === 'yes').length;
      const missingFields = getMissingGroupFields({ ...group, invited });
      return {
        ...group,
        invited,
        totalCount: invited.length,
        registeredCount,
        refusedCount,
        pendingCount: invited.length - registeredCount - refusedCount,
        pmrCount,
        parkingCount,
        draftCount: invited.filter((i) => i.isDraft).length,
        missingFields,
        isIncomplete: missingFields.length > 0,
      };
    });
  }, [rawGroups, guestsField]);

  return {
    groups,
    isLoading,
    refetch,
  };
}

/**
 * Regroupe les groupes par créneau (`slot`), triés par date de création croissante
 * à l'intérieur de chaque créneau. Les créneaux sont eux-mêmes triés chronologiquement.
 */
export function useGroupsBySlot(
  groups: GroupWithInvited[],
): { slot: string; groups: GroupWithInvited[] }[] {
  return useMemo(() => {
    const bySlot = groupBy(groups, (group) => group.slot || '');
    return orderBy(
      Object.entries(bySlot).map(([slot, slotGroups]) => ({
        slot,
        groups: orderBy(slotGroups, ['createdAt'], ['asc']),
      })),
      ['slot'],
      ['asc'],
    );
  }, [groups]);
}
